import { Box, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TimeUnitInput from '../../../../components/TimeUnitInput/TimeUnitInput';
import { LANG_TEXT } from '../../../../constants/language';
import { PreorderRuleState, TimeUnit } from '../../PreorderRules.types';
import CardLayout from '../card/CardLayout';
import { useStyles } from './styles';

interface PreorderRuleProps {
  onChange?: (values: PreorderRuleState) => void;
  onReset?: () => void;
  initialValues: PreorderRuleState;
  resetKey?: number;
}

export default function PreorderRule({ onChange, onReset, initialValues, resetKey = 0 }: PreorderRuleProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  const [currentValues, setCurrentValues] = useState<PreorderRuleState>(initialValues);
  const initialValuesRef = useRef<PreorderRuleState>(initialValues);
  const isFirstMount = useRef(true);

  // Update initial values when prop changes
  useEffect(() => {
    if (initialValues) {
      initialValuesRef.current = initialValues;
      setCurrentValues(initialValues);
      isFirstMount.current = true; // Reset first mount flag when props change
    }
  }, [initialValues, resetKey]);

  // Notify parent of changes
  useEffect(() => {
    const hasChanged = JSON.stringify(currentValues) !== JSON.stringify(initialValuesRef.current);

    if (isFirstMount.current || hasChanged) {
      onChange?.(currentValues);
      isFirstMount.current = false;
    }
  }, [currentValues]);

  const handleAcceptanceSinceChange = useCallback((next: { value: number; unit: TimeUnit; pickerValue?: string }) => {
    setCurrentValues((prev) => ({
      ...prev,
      acceptanceSince: {
        value: next.value,
        unit: next.unit,
        pickerValue: next.pickerValue || prev.acceptanceSince.pickerValue,
      },
    }));
  }, []);

  const handleAcceptanceUntilChange = useCallback((next: { value: number; unit: TimeUnit; pickerValue?: string }) => {
    setCurrentValues((prev) => ({
      ...prev,
      acceptanceUntil: {
        value: next.value,
        unit: next.unit,
        pickerValue: next.pickerValue || prev.acceptanceUntil.pickerValue,
      },
    }));
  }, []);

  const handleTimeSlotsSinceChange = useCallback((next: { value: number; unit: TimeUnit; pickerValue?: string }) => {
    setCurrentValues((prev) => ({
      ...prev,
      timeSlotsSince: {
        value: next.value,
        unit: next.unit,
      },
    }));
  }, []);

  const handleOrderDeliverableUntilChange = useCallback((next: { value: number; unit: TimeUnit; pickerValue?: string }) => {
    setCurrentValues((prev) => ({
      ...prev,
      orderDeliverableUntil: {
        value: next.value,
        unit: next.unit,
      },
    }));
  }, []);

  const labelContent = (
    <div className={classes.labelColumn}>
      <div className={classes.labelSection}>
        <div className={classes.label}>{t(LANG_TEXT.PRE_ORDER_ACCEPTANCE)}</div>
      </div>
      <div className={classes.labelSection}>
        <div className={classes.label}>{t(LANG_TEXT.PRE_ORDER_TIME_SLOTS)}</div>
      </div>
      <div className={classes.labelSection}>
        <div className={classes.label}>{t(LANG_TEXT.ORDERS_DELIVERABLE)}</div>
        <div className={classes.note}>{t(LANG_TEXT.IN_RELATION_WITH_GAME_STARTS)}</div>
      </div>
    </div>
  );

  const actionContent = useMemo(
    () => (
      <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column' }}>
        <div className={classes.rulesContainer}>
          {/* Row 1: Pre-order acceptance */}
          <div className={classes.inputsSection}>
            <TimeUnitInput
              key={`acceptance-since-${resetKey}`}
              label1={t(LANG_TEXT.SINCE)}
              label2={t(LANG_TEXT.PRE_ORDER_ACCEPTANCE_AT)}
              defaultValue={initialValuesRef.current?.acceptanceSince?.value}
              defaultUnit={initialValuesRef.current?.acceptanceSince?.unit}
              typeProp="time"
              defaultPickerValue={initialValuesRef.current?.acceptanceSince?.pickerValue}
              onChange={handleAcceptanceSinceChange}
              allowHour={false}
              allowMinute={false}
              limit={{ min: 0, max: 9999 }}
            />
            <TimeUnitInput
              key={`acceptance-until-${resetKey}`}
              label1={t(LANG_TEXT.UNTIL)}
              label2={t(LANG_TEXT.PRE_ORDER_ACCEPTANCE_AT)}
              defaultValue={initialValuesRef.current?.acceptanceUntil?.value}
              defaultUnit={initialValuesRef.current?.acceptanceUntil?.unit}
              typeProp="time"
              defaultPickerValue={initialValuesRef.current?.acceptanceUntil?.pickerValue}
              onChange={handleAcceptanceUntilChange}
              allowHour={false}
              allowMinute={false}
              limit={{ min: 0, max: 9999 }}
            />
          </div>

          {/* Row 2: Pre-order Time slots */}
          <div className={classes.inputsSection}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <TimeUnitInput
                key={`timeslots-since-${resetKey}`}
                label1={t(LANG_TEXT.SINCE)}
                label2={t(LANG_TEXT.INCREMENTS)}
                defaultValue={initialValuesRef.current.timeSlotsSince.value}
                defaultUnit={initialValuesRef.current.timeSlotsSince.unit}
                typeProp="none"
                onChange={handleTimeSlotsSinceChange}
                allowDay={false}
                limit={{ min: 0, max: 9999 }}
              />
              <Typography className={classes.helperText}>{t(LANG_TEXT.INCREMENTS)}</Typography>
            </div>
          </div>

          {/* Row 3: Orders deliverable */}
          <div className={classes.inputsSection}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <TimeUnitInput
                key={`order-deliverable-until-${resetKey}`}
                label1={t(LANG_TEXT.UNTIL)}
                defaultValue={initialValuesRef.current.orderDeliverableUntil.value}
                defaultUnit={initialValuesRef.current.orderDeliverableUntil.unit}
                typeProp="none"
                onChange={handleOrderDeliverableUntilChange}
                allowDay={false}
                limit={{ min: 1, max: 9999 }}
              />
              <Typography className={classes.helperText}>{t(LANG_TEXT.GAME_STARTS)}</Typography>
            </div>
          </div>
        </div>
      </Box>
    ),
    [resetKey, t, classes, handleAcceptanceSinceChange, handleAcceptanceUntilChange, handleTimeSlotsSinceChange, handleOrderDeliverableUntilChange]
  );

  return <CardLayout title={t(LANG_TEXT.PREORDER_RULES)} label={labelContent} action={actionContent} />;
}
